import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import DoorCta from './DoorCta';
import useDoorPage, { studioHrefFor } from '../hooks/useDoorPage';
import { DOOR_BRINNO_FLICKER } from '../doorAttribution';
import { RETENTION_DAYS } from '../photoUpload/caps';
import { PRICING_TIERS, FREE_SIGNUP_CREDITS } from '../pricing/plans';
import { displayNameOf } from '../lib/operations';
import './DoorPage.css';

// Brinno spoke (#388). Device tail for the deflicker hub: "Brinno timelapse
// flicker", "Brinno exposure flicker fix". Brinno's construction and garden
// cameras are deployed for months and write a finished timelapse file, so the
// user arrives holding a clip that strobes and no way to re-shoot it.
//
// THE HONESTY LANDMINE ON THIS PAGE is the container. Tweedia's source-video
// upload accepts .mp4 (SourceUploadPanel/AddFootagePanel, `accept=".mp4"`), and
// several Brinno models write .avi. Telling every visitor to "just upload the
// video" would be a promise the uploader refuses, so the AVI case is called out
// in the steps, in a section of its own and in the FAQ. Numbers and the
// operation name are imported, never retyped (#378/#384, #379).

const FREE_TIER = PRICING_TIERS.find((tier) => tier.id === 'free');
const SOLO_TIER = PRICING_TIERS.find((tier) => tier.id === 'solo_founding');

const FREE_POOL_LABEL = `${FREE_TIER.storageGb} GB`;
const FIX_FLICKER = displayNameOf('deflicker');

const SECTION_IDS = [
  'door_hero',
  'door_why',
  'door_fix',
  'door_container',
  'door_camera',
  'door_free',
  'door_faq',
  'door_final_cta',
];

const TOC = [
  { id: 'door_why', label: 'Why Brinno clips flicker' },
  { id: 'door_fix', label: 'Fixing the clip you already have' },
  { id: 'door_container', label: 'If your camera wrote an AVI' },
  { id: 'door_camera', label: 'Reducing it on the next deployment' },
  { id: 'door_free', label: 'What this costs' },
  { id: 'door_faq', label: 'FAQ' },
];

const STEPS = [
  {
    name: 'Create a free account and start a project',
    text: 'No credit card and nothing to install — Tweedia runs in the browser. One project per deployment keeps the clip, the edits and the renders together.',
  },
  {
    name: 'Upload the timelapse file from the camera',
    text: 'Video goes in as .mp4. If your camera saved stills instead, upload the folder and Tweedia compiles it into a video first, ordered by the time each frame was captured.',
  },
  {
    name: `Switch on ${FIX_FLICKER} on the Edits tab`,
    text: `Your footage is measured for flicker automatically, and when it is found the card flags it and you switch on ${FIX_FLICKER}. A months-long Brinno deployment is close to a worst case for this, so it usually is flagged.`,
  },
  {
    name: 'Render and download',
    text: 'Rendering runs on our workers rather than in your browser, so you can close the tab. Free-plan renders carry a Tweedia watermark.',
  },
];

const FAQ = [
  {
    q: 'Why does my Brinno timelapse flicker?',
    a: 'Because each frame was exposed on its own, hours or days apart, and the camera made a slightly different exposure decision each time. Played back at speed, those small differences become a strobe. It is a normal consequence of shooting one frame at a time over a long period, not a fault in the camera.',
  },
  {
    q: 'Which Brinno files can I upload?',
    a: 'The timelapse video as .mp4, or the folder of stills if the camera was set to save them. Stills can be JPEG, PNG, HEIC/HEIF, TIFF, WebP or AVIF, and you can drop the card directory as-is — files that are not images are skipped and reported instead of failing the batch.',
  },
  {
    q: 'My camera saved an AVI file. Now what?',
    a: 'Tweedia takes .mp4 for video today, so an AVI needs one extra step: convert it to MP4 with any converter, or — if the camera also kept the stills — upload the photo folder instead and let Tweedia compile the video. Uploading the stills is the better option when you have them, because you choose the frame rate at compile time instead of living with what the camera baked in.',
  },
  {
    q: 'Will fixing the flicker flatten the change from morning to evening?',
    a: `No. ${FIX_FLICKER} pulls each frame's brightness toward the average of a short window of frames on either side of it, so it removes the frame-to-frame jitter and leaves the slow change in daylight across the shoot intact.`,
  },
  {
    q: 'Do I need to install software?',
    a: 'No. You sign in, upload, and render — all in a browser, on Windows, macOS, Linux or ChromeOS. The rendering happens on our servers.',
  },
  {
    q: 'Is it free?',
    a: `The account is free and includes ${FREE_TIER.storageGb} GB of pooled storage and ${FREE_SIGNUP_CREDITS} starter credits, which is what pays for rendering. Free renders carry a Tweedia watermark; ${SOLO_TIER.name} at ${SOLO_TIER.priceLabel}${SOLO_TIER.priceSuffix} removes it.`,
  },
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/guides/brinno-timelapse-flicker';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: 'How to Fix Brinno Timelapse Flicker',
        description:
          'Why Brinno timelapse clips flicker and how to smooth an existing clip in the browser with Fix flicker.',
        url,
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
      },
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: 'How to fix flicker in a Brinno timelapse',
        description:
          'Upload the Brinno timelapse to a Tweedia Studio project, switch on Fix flicker on the Edits tab, and render the smoothed video.',
        step: STEPS.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.name,
          url: `${url}#door_fix`,
          text: step.text,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

const STUDIO_HREF = studioHrefFor(DOOR_BRINNO_FLICKER);

function BrinnoFlickerGuidePage({ user, signIn }) {
  const signedIn = !!user;

  const { studioHref, setSectionRef, onCtaActivate, onNavClick } = useDoorPage({
    door: DOOR_BRINNO_FLICKER,
    signedIn,
    signIn,
    sectionIds: SECTION_IDS,
    meta: {
      title: 'Brinno Timelapse Flicker — How to Fix It | Tweedia Studio',
      description:
        'Brinno cameras meter every frame on its own, so long deployments strobe. Upload the clip, switch on Fix flicker, and render in your browser. Free account.',
      canonicalPath: '/guides/brinno-timelapse-flicker',
      jsonLd: buildJsonLd(),
    },
  });

  const renderCta = (ctaId, label, testId) => (
    <DoorCta
      ctaId={ctaId}
      testId={testId}
      signedIn={signedIn}
      studioHref={studioHref}
      onActivate={onCtaActivate}
    >
      {label}
    </DoorCta>
  );

  return (
    <div className="door-page">
      <Header user={user} signIn={signIn} />
      <main className="door-content" data-testid="door-content">
        <section
          className="door-hero"
          id="door_hero"
          data-section-id="door_hero"
          ref={setSectionRef('door_hero')}
        >
          <p className="door-eyebrow">Guide · Brinno</p>
          <h1>Brinno timelapse flicker, and how to fix it</h1>
          <p className="door-lede">
            You left the camera on a jobsite for four months, pulled the card, and the playback
            strobes. The frames are fine — the brightness jumps between them. Upload the clip to
            Tweedia Studio, switch on {FIX_FLICKER}, and render a smoothed version in your
            browser. Free account, nothing to install.
          </p>
          <div className="door-cta-row">
            {renderCta('door_brinno_hero', 'Start free — upload your clip', 'door-cta-hero')}
          </div>
          <p className="door-note">
            Free account, no credit card. Renders on the free plan carry a Tweedia watermark.
          </p>
        </section>

        <nav className="door-toc" aria-label="On this page">
          <p className="door-toc-title">On this page</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="door_why" data-section-id="door_why" ref={setSectionRef('door_why')}>
          <h2>Why Brinno clips flicker</h2>
          <p>
            A Brinno camera is built to sit still for months and take one photograph at a time.
            Every one of those exposures is metered independently, minutes or hours apart, under
            light that never repeats exactly. Two consecutive frames can therefore differ by a
            fraction of a stop for reasons that had nothing to do with the scene: a cloud, a
            work light switching on, the sun crossing a window frame.
          </p>
          <p>
            None of that is visible one photo at a time. Played back at 24 or 30 frames per
            second, those small differences land back to back and read as a strobe — the effect
            people search for as "Brinno flicker" or "exposure flicker". Long deployments make
            it worse simply because they contain more light conditions.
          </p>
          <p>
            Worth saying plainly: this is a normal consequence of shooting one frame at a time
            over a long period, and not a fault in your camera. Nothing you did on site caused
            it and no setting would have removed it entirely.
          </p>
          <p>
            The fix in post is temporal smoothing: pull each frame's brightness toward the
            average of the frames around it, so the jitter flattens while the real change in
            light survives.{' '}
            <Link to="/deflicker-timelapse" onClick={onNavClick('deflicker_door_from_brinno')}>
              The deflicker page
            </Link>{' '}
            covers the causes and the maths in more detail.
          </p>
        </section>

        <section id="door_fix" data-section-id="door_fix" ref={setSectionRef('door_fix')}>
          <h2>Fixing the clip you already have</h2>
          <ol className="door-steps" data-testid="door-steps">
            {STEPS.map((step) => (
              <li key={step.name}>
                <strong>{step.name}.</strong> {step.text}
              </li>
            ))}
          </ol>
          <p>
            Before you render, the {FIX_FLICKER} card draws the frame-to-frame brightness of
            your own clip alongside the smoothed curve the filter will produce. It is computed
            in the browser from measurements already taken, so checking costs no credits.
          </p>
        </section>

        <section
          id="door_container"
          data-section-id="door_container"
          ref={setSectionRef('door_container')}
        >
          <h2>If your camera wrote an AVI</h2>
          <p>
            Brinno models differ in what they leave on the card: some write an MP4, some write
            an AVI, and some can be set to keep the individual stills. Tweedia's video upload
            takes <strong>.mp4</strong> today, so an AVI needs one extra step.
          </p>
          <p>
            <strong>Two ways round it.</strong> Convert the AVI to MP4 with any converter and
            upload that — or, if the camera also kept the stills, upload the photo folder
            instead and let Tweedia compile the video. Uploading the stills is the better option
            when you have them: you choose the frame rate at compile time rather than living
            with the one the camera baked in, and you can rebuild the video later without going
            back to site.
          </p>
          <div className="door-crosslinks" data-testid="door-crosslinks">
            <Link
              to="/photo-sequence-to-video"
              className="door-crosslink"
              data-testid="door-link-photo-sequence"
              onClick={onNavClick('photo_sequence_door_from_brinno')}
            >
              <span className="door-crosslink-title">Photo sequence to video →</span>
              <span className="door-crosslink-text">
                How a folder of stills becomes a video, ordered by capture time.
              </span>
            </Link>
            <Link
              to="/deflicker-timelapse"
              className="door-crosslink"
              data-testid="door-link-deflicker"
              onClick={onNavClick('deflicker_door_from_brinno_crosslink')}
            >
              <span className="door-crosslink-title">Deflicker a timelapse →</span>
              <span className="door-crosslink-text">
                The main guide: causes, the fix, and what it will not do.
              </span>
            </Link>
          </div>
        </section>

        <section
          id="door_camera"
          data-section-id="door_camera"
          ref={setSectionRef('door_camera')}
        >
          <h2>Reducing it on the next deployment</h2>
          <p>
            Nothing here helps the four months already on the card, but it is worth knowing for
            the next install. Flicker tracks how much the exposure decision is allowed to move
            between frames, so anything that steadies the light or the metering reduces it: a
            framing that avoids direct sun and sky in the same shot, a scene lit consistently
            after dark rather than by lights that switch on and off, and — where your model
            offers it — a fixed rather than fully automatic exposure.
          </p>
          <p>
            Check your camera's own manual for which of those it exposes; the controls vary
            across the range. Whatever you do at capture, some flicker survives a multi-month
            shoot, which is why the post step exists.
          </p>
        </section>

        <section id="door_free" data-section-id="door_free" ref={setSectionRef('door_free')}>
          <h2>What this costs</h2>
          <ul className="door-free-list" data-testid="door-free-list">
            <li>
              <strong>{FIX_FLICKER} is not a paid feature</strong> — it is available on the free
              plan like every other operation.
            </li>
            <li>
              <strong>{FREE_POOL_LABEL} of pooled storage</strong> across unlimited projects, and
              no per-project frame limit.
            </li>
            <li>
              <strong>{FREE_SIGNUP_CREDITS} starter credits</strong> toward rendering. A longer
              or higher-resolution render uses more of them.
            </li>
            <li>
              <strong>Renders carry a Tweedia watermark</strong> on the free plan. Every plan
              exports up to 4K — the watermark, not resolution, is the free limit.
            </li>
            <li>
              <strong>Raw photos are kept {RETENTION_DAYS} days</strong> after a project's last
              activity on the free plan, then cleared.
            </li>
          </ul>
          <p>
            {SOLO_TIER.name} at {SOLO_TIER.priceLabel}
            {SOLO_TIER.priceSuffix} removes the watermark, raises the pool to{' '}
            {SOLO_TIER.storageGb} GB and keeps your raw photos while the subscription is active
            — which matters if you run several sites at once.{' '}
            <Link to="/pricing" onClick={onNavClick('pricing_from_brinno_door')}>
              See the plans
            </Link>
            .
          </p>
        </section>

        <section id="door_faq" data-section-id="door_faq" ref={setSectionRef('door_faq')}>
          <h2>FAQ</h2>
          <div className="door-faq" data-testid="door-faq">
            {FAQ.map((item) => (
              <div className="door-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="door-final-cta"
          id="door_final_cta"
          data-section-id="door_final_cta"
          ref={setSectionRef('door_final_cta')}
        >
          <h2>Bring the clip off the card</h2>
          <p>
            Upload it, look at the before-and-after brightness curve, and render when it looks
            right. Months of deployment, one browser tab.
          </p>
          <div className="door-cta-row">
            {renderCta('door_brinno_final', 'Create your free account', 'door-cta-final')}
            <Link
              to="/deflicker-timelapse"
              className="door-secondary-cta"
              data-testid="door-secondary-deflicker"
              onClick={onNavClick('deflicker_door_from_brinno_final')}
            >
              Read the deflicker guide
            </Link>
          </div>
          <p className="door-note">
            Free renders carry a Tweedia watermark. No credit card required to start.
          </p>
        </section>

        <p className="door-footnote" data-testid="door-footnote">
          Brinno is a trademark of Brinno Incorporated. Tweedia Studio is not affiliated with,
          endorsed by or sponsored by Brinno. Camera behaviour varies across models and firmware
          — check your own camera's documentation for what it records and how.
        </p>
      </main>
      <Footer />
    </div>
  );
}

export default BrinnoFlickerGuidePage;
export { FAQ, STEPS, SECTION_IDS, STUDIO_HREF };
